import { ALL_PATTERNS, levelOrder, type LevelId } from '../content/sequence';

// Spaced-repetition / mastery, per docs/03: Leitner boxes with EXPANDING intervals,
// weak items resurface, and an item isn't "mastered" until it's also FAST (speed-aware,
// gently — never punitive). Pure logic; the store holds the persisted map.

export interface MasteryState {
  /** Leitner box 0..MAX_BOX (higher = stronger, longer gap before review). */
  box: number;
  /** Session index when this pattern is next due. */
  dueSession: number;
  exposures: number;
  /** Rolling average time-to-catch+fix (ms) — the speed signal. */
  avgMs: number;
  lastSession: number;
}

export type MasteryMap = Record<string, MasteryState>;

export interface BeatResult {
  /** Clean catch (no wrong taps) AND clean rebuild (no wrong placements). */
  firstTry: boolean;
  /** Time from beat shown to fixed (ms). */
  timeMs: number;
  /** Whether the "hear it" coach was used. */
  usedHear: boolean;
}

const MAX_BOX = 4;
const MASTER_BOX = 3;
/** box -> sessions until due (expanding intervals). */
const GAPS = [1, 1, 2, 4, 8];
/** "Fast enough" for a 6yo to catch + rebuild a word — gentle; tune at play-test. */
export const SPEED_TARGET_MS = 12000;

export function initialMastery(): MasteryState {
  return { box: 0, dueSession: 0, exposures: 0, avgMs: 0, lastSession: -1 };
}

/** Update one pattern's mastery from a beat result. Returns a new map. */
export function recordResult(
  map: MasteryMap,
  patternId: string,
  session: number,
  r: BeatResult,
): MasteryMap {
  const cur = map[patternId] ?? initialMastery();
  const avgMs = cur.exposures === 0 ? r.timeMs : Math.round(cur.avgMs * 0.7 + r.timeMs * 0.3);

  let box = cur.box;
  if (!r.firstTry) {
    box = Math.max(0, box - 1); // struggled — resurface sooner
  } else if (r.timeMs <= SPEED_TARGET_MS && !r.usedHear) {
    box = Math.min(MAX_BOX, box + 1); // clean, fast, unaided — advance
  }
  // first-try but slow, or leaned on "hear it" -> hold the box (not automatic yet)

  return {
    ...map,
    [patternId]: {
      box,
      dueSession: session + (GAPS[box] ?? 1),
      exposures: cur.exposures + 1,
      avgMs,
      lastSession: session,
    },
  };
}

export function isMastered(state: MasteryState | undefined): boolean {
  return !!state && state.box >= MASTER_BOX && state.avgMs > 0 && state.avgMs <= SPEED_TARGET_MS;
}

/** Patterns due now within the active band, weakest (lowest box / never seen) first. */
export function duePatterns(map: MasteryMap, band: LevelId, session: number): string[] {
  const maxOrder = levelOrder(band);
  return ALL_PATTERNS.filter((pat) => levelOrder(pat.level) <= maxOrder)
    .map((pat) => ({ id: pat.id, st: map[pat.id] }))
    .filter(({ st }) => !st || st.dueSession <= session)
    .sort((a, b) => (a.st?.box ?? -1) - (b.st?.box ?? -1))
    .map(({ id }) => id);
}

/** True when every (non-stub) pattern in the band is mastered — time to advance a level. */
export function bandMastered(map: MasteryMap, band: LevelId): boolean {
  const maxOrder = levelOrder(band);
  const inBand = ALL_PATTERNS.filter((pat) => levelOrder(pat.level) === maxOrder);
  return inBand.length > 0 && inBand.every((pat) => isMastered(map[pat.id]));
}
